import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { log } from '@/lib/log';
import { normalizeWatchKind, watchRouteRedirect } from '@/lib/player/watchRoute';
import { loginDestination } from '@/lib/loginRedirect';

// Routes — each `view` is lazy-imported. `meta.public: true` exempts a route
// from the auth guard. The hash redirect catches legacy bookmarks like
// `#live` so existing links keep working post-cutover.

// Exported so tests can assert the route table directly — notably that /about
// carries no admin guard, which is a compliance requirement rather than a
// styling preference.
export const routes: RouteRecordRaw[] = [
    { path: '/', redirect: '/home' },
    { path: '/home', name: 'home', component: () => import('@/views/Home.vue'), meta: { title: 'Home' } },
    // `fullBleed` — mobile-foldable-redesign §2.1. Live and Guide are the two
    // pages whose layout is a CANVAS (a stage beside a list; a timeline that
    // scrolls in both axes), so below 1024 they take the whole content column
    // and `.main`'s side padding would only shrink it. Every other page keeps
    // the padded column. `App.vue`'s `<main>` reads this; nothing else does.
    { path: '/live', name: 'live', component: () => import('@/views/Live.vue'), meta: { title: 'Live TV', fullBleed: true } },
    { path: '/guide', name: 'guide', component: () => import('@/views/Guide.vue'), meta: { title: 'Guide', fullBleed: true } },
    { path: '/events', name: 'events', component: () => import('@/views/EventFinder.vue'), meta: { title: 'Event Finder' } },
    // Atlas remediation R5 — Manage workspace (Channels/Groups tabs, reorder/select).
    { path: '/manage', name: 'manage', component: () => import('@/views/Manage.vue'), meta: { title: 'Manage' } },
    { path: '/movies', name: 'movies', component: () => import('@/views/Movies.vue'), meta: { title: 'Movies' } },
    { path: '/movies/:id', name: 'movie-detail', component: () => import('@/views/MovieDetail.vue'), meta: { title: 'Movies' } },
    { path: '/series', name: 'series', component: () => import('@/views/Series.vue'), meta: { title: 'Series' } },
    { path: '/series/:id', name: 'series-detail', component: () => import('@/views/SeriesDetail.vue'), meta: { title: 'Series' } },
    // vod-variant-picker Phase 6 — cross-type favourites destination
    // (channels + films + series), closing fred-tv-audit gap #56.
    { path: '/favourites', name: 'favourites', component: () => import('@/views/Favourites.vue'), meta: { title: 'Favourites' } },
    { path: '/recordings', name: 'recordings', component: () => import('@/views/Recordings.vue'), meta: { title: 'Recordings' } },
    { path: '/settings', name: 'settings', component: () => import('@/views/Settings.vue'), meta: { title: 'Settings' } },
    { path: '/sources', name: 'sources', component: () => import('@/views/Sources.vue'), meta: { title: 'Sources' } },
    // Credits + TMDB attribution. Authenticated but deliberately NOT admin-only:
    // TMDB's terms require the notice be reachable by anyone who sees the data,
    // and every viewer sees it on the Library pages (Phase 3 §A.7).
    { path: '/about', name: 'about', component: () => import('@/views/About.vue'), meta: { title: 'About' } },
    {
        path: '/watch/:kind/:id',
        name: 'watch',
        component: () => import('@/views/Watch.vue'),
        meta: { title: 'Watch' },
        // Watch route contract (docs/plans/watch-route-playback-contract.md):
        // a single dynamic route + a pure-helper guard normalizes legacy links
        // BEFORE Watch mounts — `episode` → `series`, recording-shaped
        // `movie?src=/api/recording/:id/file` → `/watch/recording/:id`, and
        // unknown kinds bounce to /home so a bad `:kind` never reaches the
        // component as an invalid typed prop.
        beforeEnter: (to) => watchRouteRedirect(to),
        // Phase 2.7 — query params (source, series, season, url, returnPage)
        // flow into <Watch> via `useRoute().query`; the page parses them on
        // mount. `kind` is normalized (never blindly cast) — the guard above
        // guarantees only canonical kinds reach here, the `?? 'movie'` is a
        // dead-defensive fallback.
        props: (route) => ({
            kind: normalizeWatchKind(route.params.kind) ?? 'movie',
            id: route.params.id as string,
        }),
    },
    { path: '/login', name: 'login', component: () => import('@/views/Login.vue'), meta: { title: 'Sign in', public: true } },
    { path: '/pair', name: 'pair', component: () => import('@/views/Pair.vue'), meta: { title: 'Connect your TV', standalone: true } },
    { path: '/dev/logs', name: 'dev-logs', component: () => import('@/views/DevLogs.vue'), meta: { title: 'Dev — Logs' } },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Not found' },
    },
];

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

// Legacy bookmark redirect.
//
// Vue Router 4 with createWebHistory parses `https://host/#live` into
// `{ path: '/', hash: '#live' }` — `to.fullPath` may not preserve the
// fragment depending on the navigation entry, so the redirect must inspect
// `to.hash` (the canonical fragment property) and gate on `to.path === '/'`
// so legitimate intra-page anchors (`/somewhere#section`) aren't hijacked.
//
// Legacy bookmarks (legacy-public/js/app.js:315) only ever covered seven
// bare page hashes: #home, #live, #guide, #movies, #series, #recordings,
// #settings. The legacy SPA never encoded params or query in the hash, so
// no further parsing is required.
//
// Exported for unit testing — see index.test.ts.
export function legacyHashRedirect(
    to: Pick<RouteLocationNormalized, 'path' | 'hash' | 'fullPath'>,
): string | undefined {
    if (to.path !== '/') return undefined;
    if (!to.hash || to.hash.length <= 1) return undefined;
    const target = to.hash.slice(1).replace(/^\//, '');
    if (!target) return undefined;
    log.info('router.hash_redirect', { from: 'root', target: 'legacy_route' });
    return '/' + target;
}

router.beforeEach((to) => legacyHashRedirect(to));

// Auth guard — public routes pass through; protected routes need an authed
// user. We bail (return undefined) while auth is still loading so we don't
// race the initial whoami fetch and bounce the user to /login on the very
// first navigation. App.vue handles that first-load case via onMounted.
router.beforeEach((to) => {
    if (to.meta.public) return undefined;
    const auth = useAuthStore();
    if (!auth.loaded) return undefined;
    if (auth.isAuthed) return undefined;
    log.info('router.auth_redirect', { from: routeBreadcrumbName(to) });
    return { path: '/login', query: { next: to.fullPath } };
});

// D16 (ux-remediation X6) — the mirror image of the guard above. An already
// signed-in session that lands on /login (bookmark, back button, stale tab)
// has nothing to do there; bounce to /home instead of re-showing the sign-in
// form (or, worse, first-run admin setup) to someone already signed in.
// Exported as a pure predicate — same "thin guard, testable helper" pattern
// as legacyHashRedirect above — so this doesn't need the full lazy-view
// router to unit test.
export function authedLoginRedirect(
    to: Pick<RouteLocationNormalized, 'name'> & Partial<Pick<RouteLocationNormalized, 'query'>>,
    auth: { loaded: boolean; isAuthed: boolean },
): { path: string } | undefined {
    if (to.name !== 'login') return undefined;
    if (!auth.loaded) return undefined;
    if (!auth.isAuthed) return undefined;
    return { path: loginDestination(to.query?.next) };
}

router.beforeEach((to) => authedLoginRedirect(to, useAuthStore()));

// Document-title formatter. Exported so the brand suffix is unit-testable
// without driving the real (lazy-view) router. Returns undefined when there's
// no title so the caller leaves document.title untouched.
export function documentTitleFor(metaTitle: unknown): string | undefined {
    if (typeof metaTitle !== 'string' || !metaTitle) return undefined;
    return `${metaTitle} · Atlas`;
}

function routeBreadcrumbName(route: Pick<RouteLocationNormalized, 'name'>): string {
    return typeof route.name === 'string' ? route.name.slice(0, 64) : 'unnamed';
}

// Title + nav log.
router.beforeEach((to, from) => {
    const title = documentTitleFor(to.meta.title);
    if (title) document.title = title;
    log.info('router.navigate', { to: routeBreadcrumbName(to), from: routeBreadcrumbName(from) });
    return undefined;
});
